import { Row, Col, Form, Button, Input, Divider, message, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons"
import "../../styles/login.css";
import "../../styles/login-override.css";
import { useRef, useState, useEffect } from "react";
import { useLogin } from "../../hooks/auth/authentication";
import { AUTH_TOKEN_NAME } from "../../defaults/utilities";
import { getUserProfile, userStore } from "../../store/userStore";
import { Default } from "../../helpers/dom";
import Logo from "../../assets/logo.png"

const { Text } = Typography;

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const userIdRef = useRef(null);
  const passwordRef = useRef(null);

  const login = useLogin();
  const setUserState = userStore(state => state.setUser);

  const navigateToHome = () => navigate('/dashboard');

  const setCurrentUserState = async () => {
    const profile = await getUserProfile();
    setUserState(profile);
    return;
  }

  // Enhanced login handler with better UX
  const handleLogin = async (values) => {
    setLoading(true);

    try {
      // Add a small delay to show the loading animation
      await new Promise(resolve => setTimeout(resolve, 300));

      const { data } = await login(
        values?.userid || userIdRef.current.input.value,
        values?.password || passwordRef.current.input.value
      );

      if (!data || data.jwt.length < 1) {
        setLoading(false);
        message.error({
          content: 'Invalid credentials. Please check your User ID and password.',
          duration: 4,
          style: {
            marginTop: '20vh',
          },
        });
        return;
      }

      sessionStorage.setItem(AUTH_TOKEN_NAME, data.jwt);

      message.success({
        content: '🎉 Login successful! Redirecting to dashboard...',
        duration: 2,
        style: {
          marginTop: '20vh',
        },
      });

      // Shorter delay for better UX
      setTimeout(async () => {
        await setCurrentUserState();
        Default.token = data.jwt;
        setLoading(false);
        navigateToHome();
      }, 1500);

    } catch (err) {
      setLoading(false);
      console.error('Login error:', err);

      message.error({
        content: 'Login failed. Please try again or contact support if the problem persists.',
        duration: 4,
        style: {
          marginTop: '20vh',
        },
      });
    }
  }

  // Handle Enter key press for better UX
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      form.submit();
    }
  }

  // Auto-focus on the first input when component mounts
  useEffect(() => {
    if (userIdRef.current) {
      userIdRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className="container">
        <Row style={{ height: "100vh", margin: 0 }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="col-left">
            <div className="img-container">
              <img className="school-logo" src={Logo} alt="Nasarawa State University Logo" />
            </div>
            <div className="headline">
              <h1>Nasarawa State University, Keffi</h1>
              <Text>E-Bursary Management System</Text>
            </div>
            <div className="forgot-password">
              <Link to="/forgot-password" className="link">
                Forgot Password?
              </Link>
            </div>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="col-right">
            <div className="login-box login-form-container">
              <div className="header">
                <h1>Welcome Back</h1>
                <Text>Sign in to your account</Text>
              </div>
              <Form
                form={form}
                size="large"
                layout="vertical"
                onFinish={handleLogin}
                autoComplete="off"
                onKeyPress={handleKeyPress}>
                <Form.Item
                  name="userid"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your User ID',
                    },
                    {
                      min: 3,
                      message: 'User ID must be at least 3 characters',
                    },
                  ]}>
                  <Input
                    ref={userIdRef}
                    size="large"
                    prefix={<UserOutlined className="icon" style={{ color: '#8c8c8c', fontSize: '18px' }} />}
                    placeholder="Enter your User ID"
                    name="userid"
                    autoComplete="username"
                    aria-label="User ID"
                    disabled={loading}
                    style={{
                      borderColor: '#e1e8ed',
                      height: '50px',
                      fontSize: '16px'
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your password',
                    },
                    {
                      min: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  ]}>
                  <Input.Password
                    ref={passwordRef}
                    size="large"
                    prefix={<LockOutlined className="icon" />}
                    placeholder="Enter your password"
                    name="password"
                    autoComplete="current-password"
                    aria-label="Password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    disabled={loading}
                  />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                >
                  <Button
                    loading={loading}
                    size="large"
                    className="submit-button"
                    htmlType="submit"
                    block
                    disabled={loading}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </Form.Item>

                <div className="login-help">
                  <Text>Need help accessing your account?</Text>
                  <br />
                  <Link to="/forgot-password">
                    Reset Password
                  </Link>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}
